import Vue from "vue";
import store from "@continente/vue/components/store";
import UnitsConversionHelper from "../../../utilities/UnitsConversionHelper";
import {appendToUrl} from "../../../search/urlUtils";
import {getFixedValue} from "../../../utilities/unitsHelper";
import {convertUnits} from "../../../utilities/unitsHelper";
import {isMobile, setInputFilter, isIOS} from "../../../utilities/globalUtilities";
import {
    updatedQuantityCallback,
    updateProductQtyMap,
    triggerCartUpdate,
    triggerProductRemoval,
    updateCartPriceInformation,
    startSpinner,
    stopSpinner,
    updateCartSummary,
    updateCouponsSummary,
    blurQtyInput,
    getProductQtyMap,
    getGTMData
} from "../../../components/cartUtil";
import {getClosestMaxAllocation} from "../../../utilities/unitsHelper";
import {setMaxNumberOfUnitsPerSale} from "../../../components/quantityHelper";
import debounce from "lodash/debounce";

const regex = require("../../../utilities/regex");

/**
 * MiniCart Item Component
 */
const QuantityInputComponentMinicart = Vue.component("quantity-input-component-minicart", {
    template: `
    <div class="ct-tile-quantity-update quantity-update"
    :data-remove-action="resources.removeProductAction"
    :data-action="resources.updateQuantityAction"
    :data-dimension-update="resources.dimensionUpdateAction"
    :data-pid="item.id"
    :data-uuid="item.uuid"
    :data-add-delay="resources.addToCartDelay"
    :data-measure-options="item.measurementInfo.quantityConversionRates ? JSON.stringify(item.measurementInfo.quantityConversionRates) : ''"
    :data-price-available="(item.online && 'price' in item && item.price.sales.value > 0)"
    :data-has-error="this.isMaxQtyReached || this.isMinQtyReached || this.qtySelectedMoreThanOne"
    >
        <template v-show="this.prodUnits">
            <div class="quantity-input-wrapper has-units">
                <label class="visually-hidden" :for="'quantity-' + this.pid + this.additionalTag">
                    {{ resources.quantity }}
                </label>

                <input
                    :ref="'quantityInput-' + this.pid + this.additionalTag"
                    class="add-to-cart-quantity form-control pwc-form-input text-center add-to-cart-quantity-minicart"
                    :class="{'is-invalid': this.isInvalid}"
                    :data-pid="this.pid + this.additionalTag"
                    :name="'quantity-' + this.pid + this.additionalTag"
                    type="text"
                    inputmode="numeric"
                    :aria-label="resources.addToCartLabel"
                    title
                    v-model="this.displayValue"
                    @change="updateQuantity"
                    @focus="updateQtyFormat"
                    @blur="restoreQtyFormat"
                    :disabled="!this.isPriceAvailable"
                />

                <span class="quantity-icon quantity-icon--minus">
                    <button class="decrease-quantity-btn"
                        @click="updateQuantity"
                        :disabled="!this.isPriceAvailable"
                        role="button"
                        :aria-label="resources.quantityMinusLabel"
                    >
                        <span class="pointer-events--none" v-html="quantityInputIcons.minusIcon"></span>
                    </button>
                </span>

                <span class="quantity-icon quantity-icon--plus">
                    <button class="increase-quantity-btn"
                        @click="updateQuantity"
                        :disabled="this.isMaxQtyReached || !this.isPriceAvailable"
                        role="button"
                        :aria-label="resources.quantityPlusLabel"
                    >
                        <span class="pointer-events--none" v-html="quantityInputIcons.plusIcon"></span>
                    </button>
                    <span class="quantity-btn-disabled-placeholder"></span>
                </span>
            </div>

            <div class="measurement-unit-section" v-if="this.isPriceAvailable && this.prodUnits ? this.prodUnits.hasAlternativeUnit : false">
                <div class="pwc-form-radio" @click="setDimension">
                    <input type="radio"
                        title
                        class="pwc-form-check-input product-measurement-unit"
                        :data-type="this.prodUnits.selectedUnit == this.prodUnits.secondaryUnit ? 'primary' : ''"
                        :id="this.prodUnits.secondaryUnit + '-' + this.pid + '-measurement-unit-ptile' + this.additionalTag"
                        :name="'measurement-unit-' + this.pid + '-ptile' + this.additionalTag"
                        :value="this.prodUnits.secondaryUnit"
                        :checked="this.prodUnits.selectedUnit == this.prodUnits.secondaryUnit"
                    />
                    <label
                        :class="this.prodUnits.secondaryUnit + '-label pwc-form-check-label mb-0'"
                        :for="this.prodUnits.secondaryUnit + '-' + this.pid + '-measurement-unit-ptile' + this.additionalTag"
                    >
                        {{this.prodUnits.secondaryUnit}}
                    </label>
                    <span class="checkmark"></span>
                </div>

                <div class="pwc-form-radio" @click="setDimension">
                    <input type="radio"
                        title
                        class="pwc-form-check-input product-measurement-unit"
                        :data-type="this.prodUnits.selectedUnit == this.prodUnits.primaryUnit ? 'primary' : ''"
                        :id="this.prodUnits.primaryUnit + '-' + this.pid + '-measurement-unit-ptile' + this.additionalTag"
                        :name="'measurement-unit-' + this.pid + '-ptile' + this.additionalTag"
                        :value="this.prodUnits.primaryUnit"
                        :checked="this.prodUnits.selectedUnit == this.prodUnits.primaryUnit"
                    />
                    <label
                        :class="this.prodUnits.primaryUnit + '-label pwc-form-check-label mb-0'"
                        :for="this.prodUnits.primaryUnit + '-' + this.pid + '-measurement-unit-ptile' + this.additionalTag"
                    >
                        {{this.prodUnits.primaryUnit}}
                    </label>
                    <span class="checkmark"></span>
                </div>
            </div>

            <template v-if="this.isPriceAvailable">
                <span class="quantity-error pwc-span-error product-max-allocation"
                    v-if="this.isMaxQtyReached"
                >
                    {{ resources.quantityErrorMaximum }}
                </span>

                <span class="quantity-error pwc-span-product product-allocation"
                    v-if="this.qtySelectedMoreThanOne && !this.isMinQtyReached && !this.isMaxQtyReached"
                >
                    {{ resources.quantitySelected }}
                </span>

                <span class="quantity-error pwc-span-error product-min-allocation"
                    :data-pid="pid"
                    v-if="this.isMinQtyReached && !this.isMaxQtyReached"
                >
                    {{ resources.quantityErrorMinimum }}
                </span>
            </template>
        </template>
    </div>
    `,
    props: ["resources", "item", "quantityInputIcons"],
    data: function () {
        return {
            debounceUpdateQuantity: null,
            displayValue: null,
            prodUnits: null,
            minQtyToDisplay: null,
            maxQtyToDisplay: null,
            pid: null,
            additionalTag: "minicart",
            isInvalid: false,
            isMinQtyReached: false,
            isMaxQtyReached: false,
            qtySelectedMoreThanOne: false,
            isPriceAvailable: (this.item.online && "price" in this.item && this.item.price.sales.value > 0),
            canDisplayMinQtyErrorMsg: document.querySelectorAll(`.product-min-allocation[data-pid="${this.pid}"]`).length > 0,
            checkOnQtyComponent: false,
            measurementInfo: this.item.measurementInfo.quantityConversionRates || null,
            showInput: true,
            previousQuantity: store.getters.getProductInfo(this.pid, "quantity")
        };
    },
    methods: {
        /**
         * Prepares to update the product's quantity
         * @param {object} ev - the event that triggered the function
         */
        resizeInputQuantity() {
            //resize input box when it is changed
            var inputs = $(".add-to-cart-quantity.form-control.pwc-form-input.text-center");
            var initial_width_size = 8;
            var minwidth = 13;

            inputs.on("input", function () {
                var width = (initial_width_size + $(this).val().length + 2) + "ch";
                if (width < minwidth + "ch") {
                    width = minwidth + "ch";
                }
                $(this).closest(".quantity-input-wrapper.has-units").css("width", width);
            });
        },
        resizeInputQuantityInit() {
            //resize input box when initialized
            var inputFields = $(".quantity-input-wrapper.has-units");

            inputFields.each(function () {
                var input = $(this).find(".add-to-cart-quantity.form-control.pwc-form-input.text-center");
                var inputValue = input.val().length;
                var max_length = 4;

                if (max_length < inputValue) {
                    $(this).width(inputValue + 9 + "ch");
                    max_length = inputValue;
                }
            });
        },
        updateQuantity(ev) {
            const productUUID = this.$store.getters.getProductInfo(this.pid, "UUID");
            const previousUnits = this.createProductUnitsConversion();
            const inputName = `quantityInput-${this.pid}${this.additionalTag}`;
            const $minicartItem = $(ev.target).closest(".ct-tile-quantity.minicart");
            const $cartItem = $(ev.target).closest(".ct-tile-quantity.cart-tile");
            let canRemove = false;
            let originalInsertedValue = null;
            this.resizeInputQuantity();

            if (ev.target.classList.contains("increase-quantity-btn")) {
                this.prodUnits.addStep();
            } else if (ev.target.classList.contains("decrease-quantity-btn")) {
                var minQuantity = this.prodUnits.selectedUnit == this.prodUnits.primaryUnit ? this.prodUnits.minQty : convertUnits(this.prodUnits.minQty, this.prodUnits.selectedUnit, this.prodUnits.conversionRate, this.prodUnits.units.multiplier);
                var quantityValue = getFixedValue(this.$refs[inputName].value, this.prodUnits.selectedUnit);

                // trigger click on trash icon to display Anular block
                if (quantityValue <= minQuantity) {
                    if ($minicartItem && $minicartItem.length) {
                        $minicartItem.closest(".minicart-product-line").find(`.minicart-remove-product[data-pid="${this.pid}"]`).first(0).trigger("click");
                        return;
                    }

                    if ($cartItem && $cartItem.length) {
                        $cartItem.closest(".ct-cart-tile").find(`.remove-product[data-pid="${this.pid}"]`).first(0).trigger("click");
                        return;
                    }
                }

                this.prodUnits.subtractStep();

                canRemove = true;
            } else {
                let newValue = getFixedValue(this.$refs[inputName].value, this.prodUnits.selectedUnit);
                const inputValue = parseFloat(this.$refs[inputName].value);
                originalInsertedValue = newValue;

                if (inputValue != 0 && inputValue < this.prodUnits.minQty) {
                    this.canDisplayMinQtyErrorMsg = true;
                    this.isMinQtyReached = true;
                    this.prodUnits.syncFlag = {pid: this.pid};
                }

                if (!isNaN(newValue)) {
                    var previousQuantityValue = this.prodUnits.getValue();

                    if (newValue <= 0 && (($minicartItem && $minicartItem.length) || ($cartItem && $cartItem.length))) {
                        newValue = previousQuantityValue;
                    }

                    this.prodUnits.updateValue(newValue, this.prodUnits.selectedUnit);
                    canRemove = newValue == 0;

                    blurQtyInput(ev.target);

                    // If is a mobile, trigger blur to avoid input value not being sync
                    if (isMobile()) {
                        this.$refs[inputName].blur();
                    }
                } else {
                    this.displayValue = this.prodUnits.getFormattedValue();

                    return;
                }
            }

            this.displayValue = this.prodUnits.getFormattedValue();

            if (this.prodUnits.getValue() >= this.prodUnits.minQty && this.prodUnits.getValue() != previousUnits.getValue()) {
                this.debounceUpdateQuantity(productUUID, previousUnits, ev.target);
            } else if (this.prodUnits.getValue() < this.prodUnits.minQty) {
                if (canRemove) {
                    this.prodUnits.syncFlag = {pid: this.pid, remove: true};
                    this.prodUnits = null;
                    this.canDisplayMinQtyErrorMsg = false;

                    // trigger click on trash icon to display Anular block
                    if (originalInsertedValue == 0 && $cartItem && $cartItem.length) {
                        $cartItem.closest(".ct-cart-tile").find(`.remove-product[data-pid="${this.pid}"]`).first(0).trigger("click");
                        return;
                    }

                    triggerProductRemoval(this.pid, productUUID);

                    this.debounceUpdateQuantity.cancel();
                } else {
                    this.debounceUpdateQuantity(productUUID, previousUnits, ev.target);
                }
            }

            // trigger click on trash icon to display Anular block
            if (originalInsertedValue == 0 && (($minicartItem && $minicartItem.length) || ($cartItem && $cartItem.length))) {
                if ($minicartItem && $minicartItem.length) {
                    $minicartItem.closest(".minicart-product-line").find(`.minicart-remove-product[data-pid="${this.pid}"]`).first(0).trigger("click");
                } else {
                    $cartItem.closest(".ct-cart-tile").find(`.remove-product[data-pid="${this.pid}"]`).first(0).trigger("click");
                }
            }
        },
        updateQtyFormat(ev) {
            // remove selected unit
            ev.target.value = ev.target.value.slice(0, -(this.prodUnits.selectedUnit.length + 1));
            this.resizeInputQuantity();

            if ($(".minicart").hasClass("is-open") && isMobile() && !isIOS()) {
                $(".minicart-products-container").addClass("minicart-products-container--scrolled");
                setTimeout(() => ev.target.scrollIntoView(), 200);
            }
        },
        restoreQtyFormat(ev) {
            // add selected unit
            ev.target.value = ev.target.value.replace(regex.values.nonDigit, "") + " " + this.prodUnits.selectedUnit;
            $(".minicart-products-container").removeClass("minicart-products-container--scrolled");
        },
        setDimension(ev) {
            if (ev.target.classList.contains("pwc-form-check-input")) {
                const urlParams = {
                    pid: this.pid,
                    dimension: ev.target.value,
                };
                const dimensionUpdateUrl = appendToUrl(this.$store.state.dimensionUpdateUrl, urlParams);

                fetch(dimensionUpdateUrl)
                    .then(response => response.json())
                    .then(data => {
                        updateProductQtyMap(JSON.parse(data.productQtyMap) || {}, ev.target);
                        triggerCartUpdate();
                    });
            }
        },
        /**
         * Handles the product's quantity updates
         * @param {object} productUUID - the product's UUID
         * @param {object} previousUnits - the previous units to be used in case of error
         * @param {object} target - the target element
         */
        handleQuantityUpdate(productUUID, previousUnits, target) {
            const $gtmList = target.closest(".gtm-list");
            const list = $gtmList ? $gtmList.getAttribute("data-gtm-list") : null;
            const $productRow = $(`.minicart-product-row[data-pid="${this.pid}"]`).closest(".minicart-product-line");
            var {gtmIndex, gtmList} = getGTMData($productRow, list);

            const urlParams = {
                pid: this.pid,
                quantity: this.prodUnits.getValue(),
                step: this.prodUnits.step,
                uuid: productUUID,
                dimension: this.prodUnits.selectedUnit,
                isCart: $(".js-cart-page").length > 0,
                gtmList: gtmList,
                gtmIndex: gtmIndex
            };

            const newUrl = appendToUrl(this.$store.state.updateUrl, urlParams);

            startSpinner();

            const that = this;
            fetch(newUrl)
                .then(response => response.json())
                .then(data => {
                    //Updates product information for cart page
                    updateCartPriceInformation(productUUID, data);

                    if (data.responseJSON && data.responseJSON.redirectUrl) {
                        window.location.href = data.responseJSON.redirectUrl;
                    } else {
                        // basket may contain unavailable products from Ipaper (not in site catalog) or product which becomes offline.
                        // condition is needed in order to keep ability for other products qty change
                        // Updates the minQty/maxQty
                        if (data.finalQuantity >= that.prodUnits.maxQty || data.availableToSell < that.prodUnits.maxQty) {
                            let qtyToSet = (data.finalQuantity == that.prodUnits.maxQty && data.finalQuantity < data.availableToSell) ? data.finalQuantity : data.availableToSell;
                            let maxNumberOfUnitsPerSale = getClosestMaxAllocation(qtyToSet, that.prodUnits.step);
                            setMaxNumberOfUnitsPerSale(this.pid, maxNumberOfUnitsPerSale);

                            that.maxQtyToDisplay = maxNumberOfUnitsPerSale;
                        }

                        if (data.finalQuantity <= that.prodUnits.minQty) {
                            that.minQtyToDisplay = data.finalQuantity;
                            that.canDisplayMinQtyErrorMsg = true;
                        }

                        updatedQuantityCallback(data, target);
                        updateCartSummary(data);
                        updateCouponsSummary(data);
                    }

                    //code necessary for GTM - start
                    const dataLayerObject = {
                        detail : {
                            dataLayerObject: data?.dataLayerObject
                        }
                    };

                    if (data.quantityChange.isIncreased) {
                        document.dispatchEvent(new CustomEvent("dataLayer.addToCart", dataLayerObject));
                    } else {
                        document.dispatchEvent(new CustomEvent("dataLayer.removeFromCart", dataLayerObject));
                    }
                    //code necessary for GTM - end

                    blurQtyInput(target);
                    stopSpinner();
                });
        },
        /**
         * Creates a product's conversion information
         * @returns - a product's conversion information
         */
        createProductUnitsConversion() {
            const quantity = store.getters.getProductInfo(this.pid, "quantity");

            if (quantity) {
                let measurementInfo_ = store.getters.getProductMeasure(this.pid);

                if (measurementInfo_ != null) {
                    this.measurementInfo = measurementInfo_;
                }

                const productConversion = new UnitsConversionHelper(quantity, this.measurementInfo);
                store.commit("addProductConversion", {pid: this.pid, productConversion});

                return productConversion;
            }

            return null;
        },
        /**
         * Initialize the quantity input using a stored product conversion
         * for minicart/tiles to sync with the PDP/PLP pages
         * or create a new one
         */
        initializeQuantityInput() {
            const currentConversion = store.getters.getProductConversion(this.pid);

            if (this.isMaster && !this.prodUnits) {
                this.prodUnits = this.createProductUnitsConversion();

                if (this.prodUnits) {
                    this.showInput = true;
                }
            } else if (currentConversion) {
                if (this.isMaster) {
                    this.prodUnits = currentConversion;

                    const quantity = store.getters.getProductInfo(this.pid, "quantity");

                    if (quantity) {
                        this.prodUnits.updateValue(quantity, this.prodUnits.secondaryUnit, false);
                    } else if (this.showInput) {
                        this.showInput = false;
                    }

                } else {
                    this.prodUnits = currentConversion;
                }
            }

            if (this.prodUnits) {
                const currentUnit = store.getters.getProductInfo(this.pid, "selectedDimension") || "un";
                this.prodUnits.selectedUnit = currentUnit;
            }
            this.minQtyToDisplay = (this.prodUnits && !this.minQtyToDisplay) ? this.prodUnits.minQty : this.minQtyToDisplay;
            this.maxQtyToDisplay = (this.prodUnits && !this.maxQtyToDisplay) ? this.prodUnits.maxQty : this.maxQtyToDisplay;
            this.displayValue = (this.prodUnits) ? this.prodUnits.getFormattedValue() : null;

            let quantityChanged;
            if (!this.checkOnQtyComponent) {
                quantityChanged = this.maxQtyToDisplay < store.getters.getProductInfo(this.pid, "quantity");
                this.checkOnQtyComponent = true;
            } else {
                quantityChanged = (this.previousQuantity && this.maxQtyToDisplay == this.previousQuantity) || (this.previousQuantity != store.getters.getProductInfo(this.pid, "quantity"));
            }

            const eligibleForMaxQtyCheck = quantityChanged && this.previousQuantity != null;

            this.isMinQtyReached = (this.prodUnits && this.canDisplayMinQtyErrorMsg) ? this.prodUnits.getValue() == this.minQtyToDisplay : false;
            this.isMaxQtyReached = eligibleForMaxQtyCheck ? (this.prodUnits ? (this.maxQtyToDisplay && this.prodUnits.getValue() >= this.maxQtyToDisplay) : false) : false;

            let curentMeasurementInfo = store.getters.getProductMeasure(this.pid);
            if (curentMeasurementInfo && eligibleForMaxQtyCheck) {
                let quantity = store.getters.getProductInfo(this.pid, "quantity");
                if (quantity && quantity >= curentMeasurementInfo.maxNumberOfUnitsPerSale) {
                    this.isMaxQtyReached = true;
                }
            }

            if (!this.isMinQtyReached && !this.isMaxQtyReached) {
                this.qtySelectedMoreThanOne = true;
            }

            this.isInvalid = this.isMinQtyReached || this.isMaxQtyReached;
            this.previousQuantity = store.getters.getProductInfo(this.pid, "quantity");
        },
        initializeData() {
            const el = this.$el;
            // Stores the Products Map
            if (!store.state.productMap) {
                store.commit("setProductMap", getProductQtyMap());
            }

            const pid = this.item.id;
            let currentInputs = document.querySelectorAll(`.minicart .ct-tile-quantity[data-pid="${pid}"]:not(.qv)`);
            let isMaster = currentInputs.length === 1;

            // update elements to avoid conflicts with Tiles, PDP
            el.parentElement.classList.add("minicart");

            // Stores the Update URL only if isn't in the stored state yet
            if (!store.state.updateUrl) {
                store.commit("setUpdateUrl", this.resources.updateQuantityAction);
            }

            // Stores the Update URL only if isn't in the stored state yet
            if (!store.state.delayTime) {
                store.commit("setDelayTime", this.resources.addToCartDelay);
            }

            //Stores the Dimansion update URL only if isnt sored yet
            if (!store.state.dimensionUpdateUrl) {
                store.commit("setDimensionUpdateUrl", this.resources.dimensionUpdateAction);
            }

            this.isMaster = isMaster;
            this.pid = pid;
        }
    },
    mounted() {
        this.initializeData();
        const hasQuantity = store.getters.getProductInfo(this.pid, "quantity");

        if (hasQuantity) {
            this.initializeQuantityInput();
        }

        // Watches the Products Map
        store.watch(() => store.state.productMap, () => {
            this.initializeQuantityInput();
        });

        // Watches the Products Quantity
        store.watch(() => store.getters.getProductInfo(this.pid, "quantity"), (newVal, oldVal) => {
            this.resizeInputQuantityInit();
            if (newVal && newVal !== oldVal) {
                if (oldVal) {
                    this.canDisplayMinQtyErrorMsg = true;
                }
            }
        }, {deep: true});

        // Watches the Product Selected Unit
        this.$watch("prodUnits.selectedUnit", () => {
            if (this.prodUnits) {
                this.displayValue = this.prodUnits.getFormattedValue();
            }
        });

        // Watches change of quantity input with value greater than 0 and less than min.
        this.$watch("prodUnits.syncFlag", () => {
            if (this.prodUnits && this.prodUnits.syncFlag && this.prodUnits.syncFlag.pid == this.pid) {
                if (this.prodUnits.syncFlag.remove) {
                    this.canDisplayMinQtyErrorMsg = false;
                    this.isMinQtyReached = false;
                } else {
                    this.canDisplayMinQtyErrorMsg = true;
                    this.isMinQtyReached = true;
                }
            }
        });

        this.debounceUpdateQuantity = debounce(this.handleQuantityUpdate, this.$store.state.delayTime);

        // Filters the values inserted into the input
        const inputName = `quantityInput-${this.pid}${this.additionalTag}`;
        this.$nextTick(() => {
            setInputFilter(this.$refs[inputName], value => { return regex.values.floatValue.test(value); });
            this.resizeInputQuantityInit();
        });
    }
});

export { QuantityInputComponentMinicart };
